import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  checkUserActive: (userId: string) => Promise<boolean>;
}

export const useAuth = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  
  // Vérifie si un utilisateur est actif dans la base de données
  checkUserActive: async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_active')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Erreur lors de la vérification du statut actif:', error);
        return false;
      }
      
      return data && data.is_active === true;
    } catch (error) {
      console.error('Exception lors de la vérification du statut actif:', error);
      return false;
    }
  },
  
  login: async (token, user) => {
    // Vérifier si l'utilisateur est actif avant de le connecter
    const isActive = await get().checkUserActive(user.id);
    
    if (!isActive) {
      // Si l'utilisateur n'est pas actif, le déconnecter immédiatement
      await supabase.auth.signOut();
      throw new Error('Votre compte est inactif. Veuillez contacter l\'administrateur.');
    }
    
    // L'utilisateur est actif, procéder à la connexion
    set({ token, user, isAuthenticated: true });
  },
  
  logout: async () => {
    await supabase.auth.signOut();
    set({ token: null, user: null, isAuthenticated: false });
  },

  checkAuth: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      // Vérifier si l'utilisateur est actif
      const isActive = await get().checkUserActive(session.user.id);
      
      if (!isActive) {
        // Si l'utilisateur n'est pas actif, le déconnecter
        await supabase.auth.signOut();
        set({ token: null, user: null, isAuthenticated: false });
        return false;
      }
      
      set({ 
        token: session.access_token,
        user: session.user,
        isAuthenticated: true
      });
      return true;
    }
    set({ token: null, user: null, isAuthenticated: false });
    return false;
  }
}));
